// src/components/editor/SpellCheckModal.jsx
//
// Spelling checker for the Manuscript Editor Tool tab.
// Backed by an in-browser engine (see src/utils/localSpellCheck.js): we build a
// plain-text model of the editor by walking its editable text nodes, check it
// entirely client-side (no server round-trip, manuscript never leaves the
// browser), then map each match's offset/length back to a precise DOM Range so
// corrections replace ONLY the matched span — never by rewriting innerHTML
// (which would destroy the .rv-page pagination + caret).
//
// Grammar is out of scope for the local engine; when the infra can host
// LanguageTool, swap checkText() back for the languageApi proxy and grammar
// matches flow through this same UI unchanged.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  CircularProgress,
  Divider,
  TextField,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { checkText } from '../../utils/localSpellCheck';
import { HIGHLIGHT_ATTR } from '../../utils/editorSerialize';

// Text nodes inside these are non-authorial chrome and must be excluded from the
// model so their characters don't shift offsets or get "corrected".
const EXCLUDE_SELECTOR =
  '.rv-page-header, .rv-page-footer, .rv-page-number, .rv-end-spacer, .spell-error-highlight';
const BLOCK_SELECTOR = 'p, li, h1, h2, h3, h4, h5, h6, div.rv-page-body, td, th, blockquote';

const HIGHLIGHT_CLASS = 'spell-error-highlight';

// Build a plain-text representation of the editor plus a segment map that lets
// us translate a global text offset back to a concrete (node, offset) pair.
// A newline is inserted between block elements so grammar rules see sentence
// boundaries; those separators occupy offsets that no segment claims, which is
// fine because matches only ever point at real word characters.
const buildTextModel = (editor) => {
  const segments = []; // { node, textStart, len }
  let text = '';
  let lastBlock = null;

  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (parent.closest(EXCLUDE_SELECTOR)) return NodeFilter.FILTER_REJECT;
      if (parent.getAttribute && parent.getAttribute('contenteditable') === 'false') {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  let node = walker.nextNode();
  while (node) {
    const block = node.parentElement.closest(BLOCK_SELECTOR);
    if (block && block !== lastBlock && text.length > 0) {
      text += '\n';
    }
    lastBlock = block;
    segments.push({ node, textStart: text.length, len: node.nodeValue.length });
    text += node.nodeValue;
    node = walker.nextNode();
  }

  return { text, segments };
};

// Resolve a global offset to { node, offset }. Returns null if it falls in an
// inter-block separator (no owning node).
const resolvePoint = (model, globalOffset) => {
  const { segments } = model;
  // Linear scan is fine — manuscripts have thousands of segments, not millions,
  // and we only resolve two points per applied correction.
  for (const seg of segments) {
    if (globalOffset >= seg.textStart && globalOffset <= seg.textStart + seg.len) {
      return { node: seg.node, offset: globalOffset - seg.textStart };
    }
  }
  return null;
};

// Build a DOM Range spanning [offset, offset+length) in the model's text.
const rangeForMatch = (model, offset, length) => {
  const start = resolvePoint(model, offset);
  const end = resolvePoint(model, offset + length);
  if (!start || !end) return null;
  try {
    const range = document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    return range;
  } catch {
    return null;
  }
};

const SpellCheckModal = ({ open, onClose, editorRef, onApplied }) => {
  const [errors, setErrors] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ignoredWords, setIgnoredWords] = useState(() => new Set());
  const [customWord, setCustomWord] = useState('');
  const [stats, setStats] = useState({ words: 0, spelling: 0, grammar: 0 });

  // Latest text model, rebuilt on every check so offsets match the live DOM.
  const modelRef = useRef(null);

  const clearHighlights = useCallback(() => {
    const editor = editorRef?.current;
    if (!editor) return;
    editor.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((el) => {
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
      parent.normalize();
    });
  }, [editorRef]);

  const runCheck = useCallback(async () => {
    const editor = editorRef?.current;
    if (!editor) return;

    clearHighlights();
    setLoading(true);
    setError('');
    setCurrentIndex(0);

    const model = buildTextModel(editor);
    modelRef.current = model;

    try {
      const found = await checkText(model.text);
      const matches = found.filter((m) => {
        const word = model.text.slice(m.offset, m.offset + m.length).toLowerCase();
        return !ignoredWords.has(word);
      });
      setErrors(matches);
      const wordCount = (model.text.match(/[\p{L}\p{N}']+/gu) || []).length;
      setStats({
        words: wordCount,
        spelling: matches.filter((m) => m.ruleType === 'spelling').length,
        grammar: matches.filter((m) => m.ruleType === 'grammar').length,
      });
    } catch (e) {
      setError(e?.message || 'Could not run the spell check.');
      setErrors([]);
    } finally {
      setLoading(false);
    }
  }, [editorRef, ignoredWords, clearHighlights]);

  // Run when opened.
  useEffect(() => {
    if (open) runCheck();
    else clearHighlights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const currentError = errors[currentIndex];

  // Paint an inline highlight on the current match and scroll it into view.
  useEffect(() => {
    if (!open || !currentError || !modelRef.current) return;
    const editor = editorRef?.current;
    if (!editor) return;

    clearHighlights();
    const range = rangeForMatch(modelRef.current, currentError.offset, currentError.length);
    if (!range) return;
    try {
      const span = document.createElement('span');
      span.className = HIGHLIGHT_CLASS;
      // Tag as a view-only highlight so getCleanHTML() unwraps it (lossless) if
      // autosave/collab-emit runs while the checker is open.
      span.setAttribute(HIGHLIGHT_ATTR, 'spell');
      range.surroundContents(span); // works when the range is within one text node
      span.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } catch {
      // Range spans element boundaries — can't wrap cleanly; just scroll to it.
      try {
        const rect = range.getBoundingClientRect();
        if (rect && rect.height) {
          window.scrollBy({ top: rect.top - window.innerHeight / 2, behavior: 'smooth' });
        }
      } catch { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, errors, open]);

  const goNext = () => {
    if (currentIndex < errors.length - 1) setCurrentIndex(currentIndex + 1);
    else setCurrentIndex(0);
  };

  const applyReplacement = (replacement) => {
    const editor = editorRef?.current;
    if (!editor || !currentError || !modelRef.current) return;

    clearHighlights();
    const range = rangeForMatch(modelRef.current, currentError.offset, currentError.length);
    if (!range) return;

    try {
      range.deleteContents();
      range.insertNode(document.createTextNode(replacement));
      editor.normalize();
    } catch {
      return;
    }

    // Notify the editor so autosave + pagination fire.
    if (typeof onApplied === 'function') onApplied();

    // Offsets have shifted — re-run against the fresh DOM.
    runCheck();
  };

  const handleIgnore = () => {
    clearHighlights();
    goNext();
  };

  const handleIgnoreAll = () => {
    if (!currentError || !modelRef.current) return;
    const word = modelRef.current.text
      .slice(currentError.offset, currentError.offset + currentError.length)
      .toLowerCase();
    setIgnoredWords((prev) => new Set([...prev, word]));
    setErrors((prev) => {
      const next = prev.filter((m) => {
        const w = modelRef.current.text.slice(m.offset, m.offset + m.length).toLowerCase();
        return w !== word;
      });
      return next;
    });
    setCurrentIndex(0);
  };

  const handleClose = () => {
    clearHighlights();
    onClose?.();
  };

  const matchedText = currentError && modelRef.current
    ? modelRef.current.text.slice(currentError.offset, currentError.offset + currentError.length)
    : '';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: '#2d2d44', color: 'white', borderRadius: 2 } }}
    >
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon sx={{ color: '#F5D76E' }} />
          <Typography variant="h6">Spelling</Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#F5D76E' }} />
            <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.7)' }}>
              Checking spelling…
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
        ) : errors.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckIcon sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#4caf50' }}>
              No issues found!
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
              Checked {stats.words.toLocaleString()} words
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Stats and navigation */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip
                label={`${currentIndex + 1} of ${errors.length}`}
                sx={{ bgcolor: '#f44336', color: 'white' }}
              />
              {stats.spelling > 0 && (
                <Chip label={`${stats.spelling} spelling`} size="small"
                  sx={{ bgcolor: 'rgba(244,67,54,0.25)', color: 'white' }} />
              )}
              {stats.grammar > 0 && (
                <Chip label={`${stats.grammar} grammar`} size="small"
                  sx={{ bgcolor: 'rgba(79,195,247,0.25)', color: 'white' }} />
              )}
              <Box sx={{ flex: 1 }} />
              <IconButton
                onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                disabled={currentIndex === 0}
                sx={{ color: currentIndex === 0 ? 'rgba(255,255,255,0.3)' : '#F5D76E' }}
                title="Previous"
              >
                <ArrowBackIcon />
              </IconButton>
              <IconButton
                onClick={() => setCurrentIndex(Math.min(errors.length - 1, currentIndex + 1))}
                disabled={currentIndex === errors.length - 1}
                sx={{ color: currentIndex === errors.length - 1 ? 'rgba(255,255,255,0.3)' : '#F5D76E' }}
                title="Next"
              >
                <ArrowForwardIcon />
              </IconButton>
            </Box>

            {/* Current issue */}
            {currentError && (
              <Box sx={{
                bgcolor: currentError.ruleType === 'spelling'
                  ? 'rgba(244, 67, 54, 0.1)' : 'rgba(79, 195, 247, 0.1)',
                border: `1px solid ${currentError.ruleType === 'spelling' ? '#f44336' : '#4fc3f7'}`,
                borderRadius: 2, p: 2, mb: 2,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Chip
                    label={currentError.ruleType === 'spelling' ? 'Spelling' : 'Grammar'}
                    size="small"
                    sx={{
                      bgcolor: currentError.ruleType === 'spelling' ? '#f44336' : '#4fc3f7',
                      color: currentError.ruleType === 'spelling' ? 'white' : '#08283a',
                      fontWeight: 600,
                    }}
                  />
                  {matchedText && (
                    <Typography sx={{ color: 'white', fontFamily: 'monospace', fontWeight: 600 }}>
                      “{matchedText}”
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  {currentError.message}
                </Typography>
              </Box>
            )}

            {/* Suggestions */}
            <Typography variant="subtitle2" sx={{ color: '#F5D76E', mb: 1 }}>
              Suggestions:
            </Typography>
            {currentError?.replacements?.length > 0 ? (
              <List sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, mb: 2 }}>
                {currentError.replacements.map((suggestion, idx) => (
                  <ListItem
                    key={idx}
                    sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }, cursor: 'pointer' }}
                    onClick={() => applyReplacement(suggestion)}
                  >
                    <ListItemText primary={suggestion}
                      primaryTypographyProps={{ sx: { color: 'white' } }} />
                    <ListItemSecondaryAction>
                      <Button size="small" onClick={() => applyReplacement(suggestion)}
                        sx={{ color: '#4caf50' }}>
                        Replace
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                No automatic suggestions. You can type a correction below.
              </Alert>
            )}

            {/* Custom replacement */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Type correction…"
                value={customWord}
                onChange={(e) => setCustomWord(e.target.value)}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: '#F5D76E' },
                  },
                }}
              />
              <Button
                variant="contained"
                disabled={!customWord}
                onClick={() => { applyReplacement(customWord); setCustomWord(''); }}
                sx={{ bgcolor: '#5a4d99', '&:hover': { bgcolor: '#6a5da9' } }}
              >
                Replace
              </Button>
            </Box>

            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button startIcon={<SkipNextIcon />} onClick={handleIgnore}
                sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Ignore
              </Button>
              <Button onClick={handleIgnoreAll} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Ignore All
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', p: 2 }}>
        <Button startIcon={<RefreshIcon />} onClick={runCheck}
          sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Re-check
        </Button>
        <Button variant="contained" onClick={handleClose}
          sx={{ bgcolor: '#F5D76E', color: '#2d2d44', '&:hover': { bgcolor: '#e6c757' } }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpellCheckModal;
